/* ============================================================
   SHOP-582 — Burger Menu V3 (cascading panels) – JavaScript
   ============================================================ */

if (!customElements.get('burger-menu-v3')) {
  class BurgerMenuV3 extends HTMLElement {
    constructor() {
      super();

      this.overlay = this.querySelector('.burger-v3-overlay');
      this.panelsContainer = this.querySelector('.burger-v3-panels');
      this.panelL1 = this.querySelector('.burger-v3-panel--l1');
      this.panelL2 = this.querySelector('.burger-v3-panel--l2');
      this.panelL3 = this.querySelector('.burger-v3-panel--l3');
      this.headerArea = document.querySelector('.header_area');

      this.toggleBtn = document.querySelector('.burger-v3-toggle');
      this.closeBtn = this.querySelector('.burger-v3-close');

      this.isOpen = false;
      this.activeL1 = null;
      this.activeL2 = null;
      this.isMobile = false;

      this._hoverDelayL2 = null;
      this._hoverDelayL3 = null;
      this.HOVER_DELAY = 120; // ms – smooth but not too fast
      this.ROW_HEIGHT = 50; // px – per-row estimate for hidden navs (corrected to exact on hover)
      this._offsetRaf = null;
      this._headerObserver = null;
      this.portalRoot = null;
      this.lastFocusedElement = null;
      this._focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

      this._onResize = this._onResize.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onDocumentClick = this._onDocumentClick.bind(this);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('scroll', this._onScroll);
      document.removeEventListener('keydown', this._onKeyDown);
      document.removeEventListener('click', this._onDocumentClick);
      if (this._headerObserver) {
        this._headerObserver.disconnect();
        this._headerObserver = null;
      }
      if (this._offsetRaf) {
        cancelAnimationFrame(this._offsetRaf);
        this._offsetRaf = null;
      }
    }

    /* ---- open / close ---- */

    open() {
      if (this.isOpen) return;

      this._syncPanelOffset();
      this._closeCompetingOverlays();
      this.lastFocusedElement = document.activeElement;
      this.isOpen = true;

      document.documentElement.classList.add('burger-v3-open');
      document.body.classList.add('burger-v3-open');

      this.overlay.classList.add('is-open');
      this.panelsContainer.classList.add('is-open');
      this.panelL1.classList.add('is-visible');
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');

      // trap focus
      requestAnimationFrame(() => {
        const firstFocusable = this.panelL1.querySelector('a, button');
        if (firstFocusable) firstFocusable.focus();
      });
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      document.documentElement.classList.remove('burger-v3-open');
      document.body.classList.remove('burger-v3-open');
      this.overlay.classList.remove('is-open');
      this.panelsContainer.classList.remove('is-open', 'has-sublevel');

      this.panelL1.classList.remove('is-visible');
      this._hideL2();
      this._hideL3();
      this._resetExpandedState(this.panelL1);
      this._resetExpandedState(this.panelL2);
      this._closeLangDropdown();

      this._clearActive(this.panelL1);
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');

      // return focus to origin when possible
      if (this.lastFocusedElement && document.contains(this.lastFocusedElement)) {
        this.lastFocusedElement.focus();
      } else if (this.toggleBtn) {
        this.toggleBtn.focus();
      }

      this.lastFocusedElement = null;
    }

    /* ---- L1 items ---- */

    _bindL1Items() {
      const items = this.panelL1.querySelectorAll('.burger-v3-nav__link');
      items.forEach((item) => {
        const hasChildren = item.hasAttribute('data-has-children');

        if (hasChildren) item.setAttribute('aria-expanded', 'false');

        // Desktop: hover
        item.addEventListener('mouseenter', () => {
          if (this.isMobile) return;
          clearTimeout(this._hoverDelayL2);
          if (hasChildren) {
            this._hoverDelayL2 = setTimeout(() => {
              this._showL2(item);
            }, this.HOVER_DELAY);
          } else {
            // No submenu — close L2/L3 immediately and reset active state
            this._hideL3();
            this._hideL2();
            this._clearActive(this.panelL1);
            this.panelsContainer.classList.remove('has-sublevel');
          }
        });

        // Mobile / fallback: click (only items with children open a sublevel)
        if (hasChildren) {
          item.addEventListener('click', (e) => {
            if (!this.isMobile) return;
            e.preventDefault();
            this._showL2(item);
          });
        }
      });

    }

    _showL2(l1Item) {
      const childId = l1Item.dataset.childrenTarget;
      if (!childId) return;

      // hide L3 first
      this._hideL3();
      this._clearActive(this.panelL2);

      // mark L1 item active
      this._clearActive(this.panelL1);
      this._resetExpandedState(this.panelL1);
      l1Item.classList.add('is-active');
      l1Item.setAttribute('aria-expanded', 'true');

      // show the matching L2 list
      this.panelL2.querySelectorAll('.burger-v3-nav').forEach((nav) => {
        nav.style.display = nav.dataset.parentId === childId ? '' : 'none';
      });

      this.panelL2.classList.add('is-visible');
      this.panelsContainer.classList.add('has-sublevel');
      this.activeL1 = childId;

      // promo image height may not have been measurable at init — resize now
      this._syncContainerHeight();

      if (this.isMobile) {
        this.panelL1.classList.remove('is-visible');
      }
    }

    _hideL2() {
      this.panelL2.classList.remove('is-visible');
      this.activeL1 = null;
      this._resetExpandedState(this.panelL1);
    }

    /* ---- L2 items ---- */

    _bindL2Items() {
      const items = this.panelL2.querySelectorAll('.burger-v3-nav__link[data-has-children]');
      items.forEach((item) => {
        item.setAttribute('aria-expanded', 'false');

        item.addEventListener('mouseenter', () => {
          if (this.isMobile) return;
          clearTimeout(this._hoverDelayL3);
          this._hoverDelayL3 = setTimeout(() => {
            this._showL3(item);
          }, this.HOVER_DELAY);
        });

        item.addEventListener('click', (e) => {
          if (!this.isMobile) return;
          e.preventDefault();
          this._showL3(item);
        });
      });

      if (this.panelL2) {
        // Keep L2 open when mouse goes from L2 back to L1
        this.panelL2.addEventListener('mouseenter', () => {
          if (this.isMobile) return;
          clearTimeout(this._hoverDelayL2);
        });
      }
    }

    _showL3(l2Item) {
      const childId = l2Item.dataset.childrenTarget;
      if (!childId) return;

      this._clearActive(this.panelL2);
      this._resetExpandedState(this.panelL2);
      l2Item.classList.add('is-active');
      l2Item.setAttribute('aria-expanded', 'true');

      this.panelL3.querySelectorAll('.burger-v3-nav').forEach((nav) => {
        nav.style.display = nav.dataset.parentId === childId ? '' : 'none';
      });

      this.panelL3.classList.add('is-visible');
      this.activeL2 = childId;

      // promo image height may not have been measurable at init — resize now
      this._syncContainerHeight();

      if (this.isMobile) {
        this.panelL2.classList.remove('is-visible');
      }
    }

    _hideL3() {
      this.panelL3.classList.remove('is-visible');
      this.activeL2 = null;
      this._resetExpandedState(this.panelL2);
    }

    /* ---- Back buttons (mobile) ---- */

    _bindBackButtons() {
      const backToL1 = this.panelL2.querySelector('.burger-v3-back');
      if (backToL1) {
        backToL1.addEventListener('click', () => {
          this._hideL2();
          this._hideL3();
          this.panelL1.classList.add('is-visible');
          this.panelsContainer.classList.remove('has-sublevel');
          this._clearActive(this.panelL1);
        });
      }

      const backToL2 = this.panelL3.querySelector('.burger-v3-back');
      if (backToL2) {
        backToL2.addEventListener('click', () => {
          this._hideL3();
          this.panelL2.classList.add('is-visible');
          this._clearActive(this.panelL2);
        });
      }
    }

    /* ---- connectedCallback ---- */

    connectedCallback() {
      this._mountToBodyLayer();
      this._onResize();
      window.addEventListener('resize', this._onResize);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      document.addEventListener('keydown', this._onKeyDown);
      document.addEventListener('click', this._onDocumentClick);
      this._observeHeaderChanges();

      if (this.toggleBtn) {
        this.toggleBtn.addEventListener('click', () => {
          if (this.isOpen) {
            this.close();
          } else {
            this.open();
          }
        });
      }
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      this._bindL1Items();
      this._bindL2Items();
      this._bindBackButtons();
      this._bindLangToggle();
      this._bindDesktopHoverIntent();

      requestAnimationFrame(() => this._syncContainerHeight());

      // promo banner images load async (external URLs, no intrinsic size) —
      // recompute height once each finishes so the container accounts for it
      this.querySelectorAll('.burger-v3-promo__img').forEach((img) => {
        if (img.complete && img.naturalWidth) {
          requestAnimationFrame(() => this._syncContainerHeight());
        } else {
          img.addEventListener('load', () => this._syncContainerHeight(), { once: true });
        }
      });
    }

    /* ---- lang toggle ---- */

    _bindLangToggle() {
      const toggle = this.panelL1.querySelector('.burger-v3-lang-select');
      const list = this.panelL1.querySelector('.burger-v3-lang-list');
      if (!toggle || !list) return;

      toggle.setAttribute('aria-expanded', 'false');

      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = list.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(isOpen));
      });

      document.addEventListener('click', () => {
        this._closeLangDropdown();
      });
    }

    _closeLangDropdown() {
      const toggle = this.panelL1.querySelector('.burger-v3-lang-select');
      const list = this.panelL1.querySelector('.burger-v3-lang-list');
      if (!toggle || !list) return;

      list.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }

    _bindDesktopHoverIntent() {
      if (!this.panelsContainer) return;

      [this.panelL1, this.panelL2, this.panelL3].forEach((panel) => {
        if (!panel) return;
        panel.addEventListener('mouseenter', () => {
          if (this.isMobile) return;
          clearTimeout(this._hoverDelayL2);
          clearTimeout(this._hoverDelayL3);
        });
      });

      this.panelsContainer.addEventListener('mouseleave', () => {
        if (this.isMobile) return;
        clearTimeout(this._hoverDelayL2);
        clearTimeout(this._hoverDelayL3);
        this._hoverDelayL2 = setTimeout(() => {
          this._hideL2();
          this._hideL3();
          this._clearActive(this.panelL1);
          this._clearActive(this.panelL2);
          this.panelsContainer.classList.remove('has-sublevel');
        }, this.HOVER_DELAY);
      });
    }

    _closeCompetingOverlays() {
      document.querySelectorAll('details[open]').forEach((detail) => {
        detail.removeAttribute('open');
      });

      const activeCartNotification = document.querySelector('cart-notification .cart-notification.active');
      if (activeCartNotification) {
        activeCartNotification.classList.remove('active');
      }
    }

    _getFocusableElements() {
      if (!this.panelsContainer) return [];

      const visiblePanels = [this.panelL1, this.panelL2, this.panelL3].filter((panel) => {
        return panel && panel.classList.contains('is-visible');
      });

      const activePanel = visiblePanels[visiblePanels.length - 1] || this.panelL1;
      if (!activePanel) return [];

      return Array.from(activePanel.querySelectorAll(this._focusableSelector)).filter((el) => {
        return !el.hasAttribute('disabled') && el.offsetParent !== null;
      });
    }

    _trapFocus(e) {
      if (e.key !== 'Tab' || !this.isOpen) return;

      const focusables = this._getFocusableElements();
      if (!focusables.length) {
        e.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
        return;
      }

      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    /* ---- container height (set once, tallest panel wins) ---- */

    _getMaxContentHeight() {
      let maxH = 0;
      for (const panel of [this.panelL1, this.panelL2, this.panelL3]) {
        if (!panel) continue;
        let chrome = 0;
        const hdr = panel.querySelector('.burger-v3-panel__header');
        if (hdr) chrome += hdr.offsetHeight;
        const ftr = panel.querySelector('.burger-v3-panel__footer');
        if (ftr) chrome += ftr.offsetHeight;
        if (panel !== this.panelL1) {
          chrome += parseInt(getComputedStyle(document.documentElement)
            .getPropertyValue('--burger-v3-footer-space')) || 0;
        }
        /* Sum item heights, never nav.scrollHeight: the nav is flex:1, so its
           scrollHeight is the stretched height and feeds the container size back
           into the measurement — a loop that latched the menu open. Summing items
           is idempotent and keeps tallest-panel-wins stable across loads. */
        let tallestNav = 0;
        panel.querySelectorAll('.burger-v3-nav').forEach((nav) => {
          /* Briefly un-hide non-active navs (synchronous, restored before any
             repaint) so every list is measured exactly rather than estimated —
             the tallest sublevel then sizes the container once, for good. */
          const wasHidden = nav.style.display === 'none';
          if (wasHidden) nav.style.display = 'block';
          const navCs = getComputedStyle(nav);
          let navH = (parseFloat(navCs.paddingTop) || 0) + (parseFloat(navCs.paddingBottom) || 0);
          nav.querySelectorAll(':scope > .burger-v3-nav__item').forEach((item) => {
            // A laid-out item reports its real rendered height (list items are not
            // flex-stretched). If still 0 (e.g. unloaded promo image), fall back
            // to a per-row / derived-promo estimate.
            if (item.offsetHeight) {
              navH += item.offsetHeight;
            } else {
              const promo = item.querySelector('.burger-v3-promo');
              navH += promo ? this._promoHeight(promo) : this.ROW_HEIGHT;
            }
          });
          if (wasHidden) nav.style.display = 'none';
          tallestNav = Math.max(tallestNav, navH);
        });
        maxH = Math.max(maxH, chrome + tallestNav);
      }
      /* +4px absorbs sub-pixel rounding so no hairline scrollbar slips in. */
      return Math.ceil(maxH) + 4;
    }

    // Inline promo <li> may be hidden (parent nav display:none) so its
    // scrollHeight reads 0. Derive rendered height from the image's intrinsic
    // ratio scaled to the nav's content width, plus the <li>'s own padding.
    _promoHeight(promo) {
      const img = promo.querySelector('.burger-v3-promo__img');
      if (!img || !img.naturalWidth) return this.ROW_HEIGHT;
      const li = promo.closest('.burger-v3-nav__item') || promo;
      const liCs = getComputedStyle(li);
      const padY = (parseFloat(liCs.paddingTop) || 0) + (parseFloat(liCs.paddingBottom) || 0);
      const nav = promo.closest('.burger-v3-nav');
      let navW = 340;
      let navPadX = 40;
      if (nav) {
        const navCs = getComputedStyle(nav);
        navPadX = (parseFloat(navCs.paddingLeft) || 0) + (parseFloat(navCs.paddingRight) || 0);
        // hidden nav reports clientWidth 0 — fall back to panel width
        navW = nav.clientWidth || (nav.closest('.burger-v3-panel') || {}).clientWidth || 340;
      }
      const liPadX = (parseFloat(liCs.paddingLeft) || 0) + (parseFloat(liCs.paddingRight) || 0);
      const contentW = navW - navPadX - liPadX;
      return Math.round(contentW * (img.naturalHeight / img.naturalWidth)) + padY;
    }

    _syncContainerHeight() {
      if (!this.panelsContainer) return;
      const topPx = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--burger-v3-top')
      ) || 0;
      const maxViewport = window.innerHeight - topPx - 20;
      const contentH = this._getMaxContentHeight();
      /* One constant height (tallest sublevel): no per-hover resize, no scroll. */
      this.panelsContainer.style.height = Math.min(contentH, maxViewport) + 'px';
    }

    /* ---- helpers ---- */

    _clearActive(panel) {
      if (!panel) return;
      panel.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
    }

    _resetExpandedState(panel) {
      if (!panel) return;
      panel.querySelectorAll('.burger-v3-nav__link[data-has-children]').forEach((el) => {
        el.setAttribute('aria-expanded', 'false');
      });
    }

    _syncPanelOffset() {
      if (!this.headerArea || this.isMobile) {
        document.documentElement.style.setProperty('--burger-v3-top', '0px');
        document.documentElement.style.setProperty('--burger-v3-left', '0px');
        return;
      }

      if (this.toggleBtn) {
        const toggleRect = this.toggleBtn.getBoundingClientRect();
        const top = Math.max(0, Math.round(toggleRect.top));
        const left = Math.max(0, Math.round(toggleRect.left));
        document.documentElement.style.setProperty('--burger-v3-top', `${top}px`);
        document.documentElement.style.setProperty('--burger-v3-left', `${left}px`);
      } else {
        const rect = this.headerArea.getBoundingClientRect();
        const top = Math.max(0, Math.round(rect.bottom));
        document.documentElement.style.setProperty('--burger-v3-top', `${top}px`);
        document.documentElement.style.setProperty('--burger-v3-left', '0px');
      }
    }

    _schedulePanelOffsetSync() {
      if (this._offsetRaf) return;
      this._offsetRaf = requestAnimationFrame(() => {
        this._offsetRaf = null;
        this._syncPanelOffset();
      });
    }

    _mountToBodyLayer() {
      if (!this.overlay || !this.panelsContainer) return;
      if (this.portalRoot && this.portalRoot.isConnected) return;

      this.portalRoot = document.createElement('div');
      this.portalRoot.className = 'burger-v3-portal-root';
      document.body.appendChild(this.portalRoot);
      this.portalRoot.appendChild(this.overlay);
      this.portalRoot.appendChild(this.panelsContainer);
    }

    _observeHeaderChanges() {
      const headerSection = document.getElementById('shopify-section-header');
      if (!headerSection || typeof MutationObserver === 'undefined') return;

      this._headerObserver = new MutationObserver(() => this._schedulePanelOffsetSync());
      this._headerObserver.observe(headerSection, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }

    _onResize() {
      this.isMobile = window.innerWidth < 1011;
      this._schedulePanelOffsetSync();
      this._syncContainerHeight();
    }

    _onScroll() {
      if (this.isMobile) return;
      this._schedulePanelOffsetSync();
    }

    _onKeyDown(e) {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
        return;
      }

      this._trapFocus(e);
    }

    _onDocumentClick(e) {
      if (!this.isOpen) return;

      const target = e.target;
      const clickedInsidePanels = this.panelsContainer && this.panelsContainer.contains(target);
      const clickedToggle = this.toggleBtn && this.toggleBtn.contains(target);

      if (!clickedInsidePanels && !clickedToggle) {
        this.close();
      }
    }
  }

  customElements.define('burger-menu-v3', BurgerMenuV3);
}
